/**
 * The Background Sync tag, shared by the worker and the page.
 *
 * Its own module because the two sides cannot import from each other: pulling it
 * from `sw.ts` would drag `precacheAndRoute` and `self.__WB_MANIFEST` into the
 * page bundle, and pulling it from `service-worker.ts` would drag `document`
 * into the worker. A one-line module is the cheapest thing that keeps them
 * agreeing.
 *
 * One tag, so repeated registrations coalesce into a single pending sync rather
 * than queueing dozens.
 */
export const SYNC_TAG = 'signapass-stamp-queue';

/**
 * Whether a completed flush leaves the sync unfinished.
 *
 * The Background Sync contract is that a rejected `waitUntil` means "not done,
 * try again" and the browser reschedules with its own backoff. Resolving on an
 * incomplete flush tells the browser the work succeeded, and the queued stamps
 * then sit until a page happens to open — the exact case the API exists to fix.
 *
 * The subtle half is that a *successful* flush can leave rows behind. A `RETRY`
 * row — a cooldown, a daily cap, a programme mid-version-swap, all routine —
 * means the request worked and the server answered; the work is still not
 * finished, and from a page that no longer exists only the browser can decide
 * when to ask again.
 *
 * A `REJECTED` row is deleted client-side, so it never keeps a sync alive.
 *
 * Here rather than inline in the handler so it can be tested without a
 * `ServiceWorkerGlobalScope`: the decision is the thing worth pinning, and a
 * test that needed a worker harness would most likely not have been written.
 */
export function syncIsIncomplete(outcome: { remaining: number }): boolean {
  return outcome.remaining > 0;
}
